import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminAudienceViewDto,
  GfFlyerReviewComment,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";

export interface Author {
  name: string;
  initials: string;
  isAdmin: boolean;
  isAnonymous?: boolean;
  isUnknown?: boolean;
}

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export type AuthorResolver = (c: GfFlyerReviewComment) => Author;

export const useAuthorResolver = (
  audiences: AdminAudienceViewDto[],
): AuthorResolver => {
  const { t } = useTranslation();
  const adminLabel = t(messages.Admin());
  const anonymousLabel = t(messages.Anonymous_Reviewer());
  const unknownLabel = t(messages.Unknown());

  return useCallback(
    (c: GfFlyerReviewComment): Author => {
      if (c.createdByAdminId) {
        return { name: adminLabel, initials: "AD", isAdmin: true };
      }
      if (c.audienceId) {
        const match = audiences.find((a) => a.id === c.audienceId);
        if (match?.displayName) {
          return {
            name: match.displayName,
            initials: initialsFromName(match.displayName),
            isAdmin: false,
          };
        }
        return {
          name: unknownLabel,
          initials: "?",
          isAdmin: false,
          isUnknown: true,
        };
      }
      return {
        name: anonymousLabel,
        initials: "?",
        isAdmin: false,
        isAnonymous: true,
      };
    },
    [audiences, adminLabel, anonymousLabel, unknownLabel],
  );
};

export const useResolvedByResolver = (
  audiences: AdminAudienceViewDto[],
): ((c: GfFlyerReviewComment) => Author | undefined) => {
  const { t } = useTranslation();
  const adminLabel = t(messages.Admin());
  const anonymousLabel = t(messages.Anonymous_Reviewer());
  const unknownLabel = t(messages.Unknown());

  return useCallback(
    (c: GfFlyerReviewComment): Author | undefined => {
      if (c.resolvedByAdminId) {
        return { name: adminLabel, initials: "AD", isAdmin: true };
      }
      if (c.resolvedByAudienceId) {
        const match = audiences.find((a) => a.id === c.resolvedByAudienceId);
        if (match?.displayName) {
          return {
            name: match.displayName,
            initials: initialsFromName(match.displayName),
            isAdmin: false,
          };
        }
        return {
          name: unknownLabel,
          initials: "?",
          isAdmin: false,
          isUnknown: true,
        };
      }
      return {
        name: anonymousLabel,
        initials: "?",
        isAdmin: false,
        isAnonymous: true,
      };
    },
    [audiences, adminLabel, anonymousLabel, unknownLabel],
  );
};
